import React, { useEffect } from 'react';
import { useLocation, useLoaderData } from 'react-router-dom';
import { VA_FORM_IDS } from 'platform/forms/constants';
import { VaBreadcrumbs } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { dashboardBC, DASHBOARD_BC_LABEL } from '../utilities/poaRequests';
import Unauthorized from '../components/Dashboard/Unauthorized';
import Authorized from '../components/Dashboard/Authorized';
import PivSelfClaim from '../components/Dashboard/PivSelfClaim/PivSelfClaim';
import api from '../utilities/api';
import { userPromise } from '../utilities/auth';

const renderUnauthorizedContent = ({ pivClaimEligible, hasInProgressForm }) => {
  if (pivClaimEligible) return <PivSelfClaim />;
  return <Unauthorized hasInProgressForm={hasInProgressForm} />;
};

const DashboardPage = props => {
  const { title } = props || {};
  const location = useLocation();
  const loaderData = useLoaderData();
  const isAuthorized = loaderData?.authorized === true;
  const hasInProgressForm = loaderData?.hasInProgressForm === true;
  const pivClaimEligible = loaderData?.pivClaimEligible === true;
  const pivClaimLinked = location.state?.pivClaimLinked === true;
  localStorage.setItem('userAuthorized', isAuthorized);
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);
  return (
    <section className="dashboard">
      <div className="arp-container">
        <VaBreadcrumbs
          breadcrumbList={dashboardBC}
          label={DASHBOARD_BC_LABEL}
          homeVeteransAffairs={false}
          wrapping
        />
        {pivClaimLinked && isAuthorized && (
          <va-alert
            status="success"
            visible
            data-testid="piv-claim-linked-alert"
            class="vads-u-margin-bottom--3"
          >
            <h2 slot="headline">Your PIV Card is now ready for use</h2>
            <p className="vads-u-margin-y--0">
              Your PIV Card has now been associated with your accreditation. You
              may now sign in to ARP using your PIV.
            </p>
          </va-alert>
        )}
        {!isAuthorized ? (
          renderUnauthorizedContent({ pivClaimEligible, hasInProgressForm })
        ) : (
          <Authorized hasInProgressForm={hasInProgressForm} />
        )}
      </div>
    </section>
  );
};

DashboardPage.loader = async ({ request }) => {
  const { searchParams } = new URL(request.url);
  const unauthorizedParam = searchParams.has('unauthorized');

  // The user is already resolved by the route's sign-in guard; `inProgressForms`
  // comes from the `/user` response and tells us if a 21a form is in progress.
  const user = await userPromise;
  const hasInProgressForm = !!user?.inProgressForms?.some(
    form => form.form === VA_FORM_IDS.FORM_21A,
  );

  // If explicitly marked unauthorized in query params, skip the server call
  // that would otherwise redirect us right back here, and instead check
  // whether the PIV self-service claim flow is available.
  if (unauthorizedParam) {
    let pivClaimEligible = false;
    try {
      const eligibilityRes = await api.checkPivClaimEligibility();
      if (eligibilityRes?.status === 204) {
        return { authorized: true, hasInProgressForm };
      }
    } catch (err) {
      if (err instanceof Response && err.status === 401) throw err;
      if (err instanceof Response && err.status === 403) {
        const body = await err.json().catch(() => ({}));
        pivClaimEligible = body?.pivClaimEligible === true;
      }
    }
    return { authorized: false, hasInProgressForm, pivClaimEligible };
  }

  const res = await api.checkAuthorized();
  // Bubble up 401 to the route guard so it can redirect to sign-in
  if (res.status === 401) throw res;
  // 403 → unauthorized
  if (res.status === 403) return { authorized: false, hasInProgressForm };
  // 204 → authorized
  if (res.status === 204) return { authorized: true, hasInProgressForm };
  // On other/unexpected status, render Unauthorized to be safe
  return { authorized: false, hasInProgressForm };
};

export default DashboardPage;
